import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AnimatedNumber from "animated-number-react";
import { ProgressBar } from "react-step-progress-bar";
import FlipMove from "react-flip-move";
import EmptyBanner from "../../../components/EmptyBanner";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { useAuth } from "../../../providers/AuthProvider";
import { PictureOutlined, QuestionCircleFilled } from "@ant-design/icons";
import { Popconfirm } from "antd";
import ProjectPosition from "../../../models/ProjectPosition";
import HighestRankIcon from "../../../components/HighestRankIcon";
import CircleProgress from "../../../components/CircleProgress";
import NEWSkillFormButton from '../../profiles/components/NEWSkillFormButton';

const MetricFormSection = ({ header, propName, userPropName, ...props }) => {

    const { auth } = useAuth();
    const initItems = (items = []) => {
        if (!auth) {
            return [];
        }

        if (items && !TB.isArray(items)) {
            items = [items];
        }

        let userSelectedItems = TB.keyBy(
            auth[userPropName] || [], 'id'
        )

        items = TB.map(items, item => {
            if (userSelectedItems[item.id]) {
                return {
                    ...item,
                    ...userSelectedItems[item.id],
                    is_selected: true
                };
            }
            return item;
        })

        return TB.orderBy(items, item =>
            TB.isDefined(item.self_score) ? 0 : 1, 'asc'
        );
    }

    const [formItems, setFormItems] = useState(initItems(props.job[propName]));
    const [showAllItems, setShowAllItems] = useState(false);
    const minVisible = Math.max(TB.filter(formItems, ['is_selected', true]).length + 2, 15)

    return (
        <div className={TB.mapClass("flex flex-col items-stretch gap-3 flex-grow", {
            "px-2": props.includeSubheader
        })}>
            {props.includeSubheader && (
                <p className="font-semibold text-xs mb-2 uppercase text-primary">
                    {TB.titleCase(propName)}
                </p>
            )}
            <div className="form-section">
                <h5>
                    {header}
                </h5>
                <div className="discipline-skills-container skill-importance-container gap-3">
                    {formItems?.length > 0 ? (
                        <FlipMove typeName={null}>
                            {TB.map((showAllItems ? formItems : TB.take(formItems, minVisible)),
                                (item, index) => (
                                    <div key={`${propName}-${item.id}-${item.self_score}-${index}`}>
                                        <NEWSkillFormButton
                                            skill={item}
                                            disabled={props.disabled}
                                            disableRemove
                                            className={item.category && "min-w-[165px]"}
                                            scoringOptions={item.category ? laravel.talent_scoring_options_by_category[item.category] : laravel.talent_scoring_options}
                                            type={item.category ? 'info' : 'default'}
                                            noMatch={TB.isNil(item.self_score)}
                                            onChange={async (model) => {
                                                if (!props.job.is_match) {
                                                    return TB.toast('You don\'t have the required role', { theme: 'danger' })
                                                }

                                                if (item.self_score && item.self_score === model.self_score) {
                                                    return;
                                                }
                                                let updatedFormItems = [...formItems];

                                                updatedFormItems[index] = { ...model };

                                                updatedFormItems = TB.orderBy(updatedFormItems, item =>
                                                    TB.isDefined(item.self_score) ? 0 : 1, 'asc'
                                                );

                                                await setFormItems(updatedFormItems);

                                                let request = await props.onUpdateItem({ [propName]: [{ ...model }] })

                                                if (!request) {
                                                    await setFormItems(initItems(props.job[propName]))
                                                }
                                            }}
                                        />
                                    </div>
                                )
                            )}
                            {!showAllItems && formItems?.length > minVisible && (
                                <div key={`${propName}-10000`} className="self-center mb4">
                                    <button onClick={() => setShowAllItems(true)} className="btn-link btn-link-primary">
                                        Show {TB.pluralize(`more ${propName}`, formItems?.length - minVisible, true)}
                                    </button>
                                </div>
                            )}
                        </FlipMove>
                    ) : (
                        <EmptyBanner message={`No Required ${TB.capitalize(propName)} Listed`} />
                    )}
                </div>

                {props.children}
            </div>
        </div>
    )
}

const JobMatchForm = props => {


    const [disabled, setDisabled] = useState(false);
    const { auth } = useAuth();

    const [job, setJob] = useState(props.job);

    const isDisabled = (props.disabled || disabled);

    const authBrandIds = TB.map(auth.brands || [], 'id')

    const jobBrands = useMemo(() => {
        return TB.orderBy(
            props.job.brands || [],
            brand => authBrandIds?.includes(brand.id),
            'desc'
        )
    }, [authBrandIds, isDisabled])

    const metrics = useMemo(() => {
        return job.matchScoresForCriteria(job)
    }, [job, isDisabled]);

    const onUpdateItem = async (updates) => {

        await setDisabled(true)
        try {
            let updatedJob = await props.onRequest(async () => {
                return auth.updateForProjectPositionMatching(job, updates);
            })
            if (!updatedJob) {
                await TB.showServerErrorToast();
                await setDisabled(false)
                return false
            }

            await props.onUpdateJob(updatedJob);
            await setJob(updatedJob)
            await setDisabled(false)
            await TB.sysAlert('talent.update_job_matching_success');

            return true;
        } catch (e) {
            console.warn(e)
            await setDisabled(false)
            return false
        }

    }


    return (

        <div className={TB.mapClass("job-match-scores-container row minimal", {
            disabled: isDisabled
        })}>

            {(isDisabled) && (
                <div className="match-updating">
                    <LoadingSpinner size={'lg'} />
                </div>
            )}

            {metrics?.overall_match && (
                <div className="job-match-header">
                    <div className="score-progress">
                        <p className="overall-progress">
                            <AnimatedNumber
                                value={TB.round(metrics?.overall_match.percent, 2)}
                                formatValue={(value) => `${TB.round(parseInt(value), 2)}% Total Match`}
                                duration={1000} />
                        </p>

                        <ProgressBar
                            filledBackground={TB.config('color.orange')}
                            height={20}
                            percent={TB.round(metrics?.overall_match.percent, 2)}
                            unfilledBackground={TB.config('color.gray')} />
                    </div>
                </div>
            )}
            <h3>
                My Relevant Experience
            </h3>
            <div className="job-match-scores col col-auto">
                <h4>Skill Requirements</h4>
                <div className="match-section">
                    <div className="metric-job-scores">
                        <div className={TB.mapClass("job-metric-match", {
                            disabled: metrics.skills_match?.skills_max_count === 0,
                        })}>
                            <CircleProgress
                                className="metric-circle"
                                progress={metrics.skills_match?.skills_max_count === 0 ? 0 : metrics.skills_match?.percent}
                                strokeWidth={7}>
                                {/*{metrics.skills_match?.max_count > 0 && metrics.skills_match?.is_ranked_highest && (*/}
                                {/*    <HighestRankIcon/>*/}
                                {/*)}*/}
                                {metrics.skills_match?.skills_max_count > 0 ? (
                                    <p>
                                        <span>
                                            <span>
                                                {metrics.skills_match?.skills_max_count > 0 ? Math.ceil(metrics.skills_match?.percent) : 0}%
                                            </span>
                                            <small>Match</small>
                                        </span>
                                    </p>
                                ) : (
                                    <p>No Criteria Set</p>
                                )}
                            </CircleProgress>
                        </div>
                    </div>
                    <MetricFormSection
                        {...props}
                        propName="skills"
                        header="Skill Requirements"
                        userPropName="skills"
                        propIndex={1}
                        disabled={isDisabled}
                        onUpdateItem={onUpdateItem}
                    />
                </div>
                <h4>Total Experience</h4>

                <div className="match-section">
                    <div className="metric-job-scores">
                        <div className={TB.mapClass("job-metric-match", {
                            disabled: metrics.experience_match?.disciplines_max_count === 0,
                        })}>
                            <CircleProgress
                                className="metric-circle"
                                progress={metrics.experience_match?.disciplines_max_count === 0 ? 0 : metrics.experience_match?.percent}
                                strokeWidth={7}>
                                {/*{metrics.experience_match?.disciplines_max_count > 0 && metrics.experience_match?.is_ranked_highest && (*/}
                                {/*    <HighestRankIcon/>*/}
                                {/*)}*/}
                                {metrics.experience_match?.disciplines_max_count > 0 ? (
                                    <p>
                                        <span>
                                            <span>
                                                {metrics.experience_match?.disciplines_max_count > 0 ? Math.ceil(metrics.experience_match?.percent) : 0}%
                                            </span>
                                            <small>Match</small>
                                        </span>
                                    </p>
                                ) : (
                                    <p>No Criteria Set</p>
                                )}
                            </CircleProgress>
                        </div>
                    </div>
                    <MetricFormSection
                        {...props}
                        propIndex={2}
                        propName="disciplines"
                        header="Total Experience"
                        userPropName="talent_disciplines"
                        valuePropName={undefined}
                        disabled={isDisabled}
                        onUpdateItem={onUpdateItem}
                    />
                </div>
                <h4>Industry Requirements</h4>
                <div className="match-section">
                    <div className="metric-job-scores">
                        <div className={TB.mapClass("job-metric-match", {
                            disabled: metrics.industries_brands_match?.industries_max_count === 0,
                        })}>
                            <CircleProgress
                                className="metric-circle"
                                progress={metrics.industries_brands_match?.industries_max_count === 0 ? 0 : metrics.industries_brands_match?.industries_percent}
                                strokeWidth={7}>
                                {/*{metrics.industries_brands_match?.total_max_count > 0 && metrics.industries_brands_match?.is_ranked_highest && (*/}
                                {/*    <HighestRankIcon/>*/}
                                {/*)}*/}
                                {metrics.industries_brands_match?.industries_max_count > 0 ? (
                                    <p>
                                        <span>
                                            <span>
                                                {metrics.industries_brands_match?.industries_max_count > 0 ?
                                                    Math.ceil(metrics.industries_brands_match?.industries_percent) : 0
                                                }%
                                            </span>
                                            <small>Match</small>
                                        </span>
                                    </p>
                                ) : (
                                    <p>No Criteria Set</p>
                                )}
                            </CircleProgress>
                        </div>
                    </div>

                    <MetricFormSection
                        {...props}
                        propIndex={3}
                        propName="industries"
                        header="Industry Requirements"
                        userPropName="industries_worked"
                        disabled={isDisabled}
                        onUpdateItem={onUpdateItem}
                    />

                </div>


                {props.job?.brands?.length > 0 && metrics.industries_brands_match?.brands_max_count > 0 && (
                    <React.Fragment>
                        <h4>Previous Brand Experience</h4>
                        <div className="match-section">
                            <div className="metric-job-scores">
                                <div className={TB.mapClass("job-metric-match", {
                                    disabled: metrics.industries_brands_match?.brands_max_count === 0,
                                })}>
                                    <CircleProgress
                                        className="metric-circle"
                                        progress={metrics.industries_brands_match?.brands_max_count === 0 ? 0 : metrics.industries_brands_match?.brands_percent}
                                        strokeWidth={7}>
                                        {/*{metrics.industries_brands_match?.total_max_count > 0 && metrics.industries_brands_match?.is_ranked_highest && (*/}
                                        {/*    <HighestRankIcon/>*/}
                                        {/*)}*/}
                                        {metrics.industries_brands_match?.brands_max_count > 0 ? (
                                            <p>
                                                <span>
                                                    <span>
                                                        {metrics.industries_brands_match?.brands_max_count > 0 ?
                                                            Math.ceil(metrics.industries_brands_match?.brands_percent) : 0
                                                        }%
                                                    </span>
                                                    <small>Match</small>
                                                </span>
                                            </p>
                                        ) : (
                                            <p>No Criteria Set</p>
                                        )}
                                    </CircleProgress>
                                </div>
                            </div>

                            <div className="flex-grow flex flex-row flex-wrap items-center gap-x-6 gap-y-4 px-[15px]">
                                {TB.map(jobBrands, (brand, index) => {

                                    const isProfileMatch = TB.find(auth.brands, ['id', brand.id])
                                    return (
                                        <div key={`brand-${index}-${brand.id}`}
                                            id={`brand-${index}-${brand.id}`}
                                            className={TB.mapClass("border inline-flex flex-row justify-start items-center relative gap-4 py-3 pl-4 pr-8 min-w-[200px] h-28", {
                                                "bg-gray-50": isProfileMatch
                                            })}>

                                            <div className="flex items-center justify-start">
                                                {(brand.logo_path || brand.icon_path) ?
                                                    <img className="h-full max-h-14 w-full max-w-24 object-contain"
                                                        src={brand.logo_path || brand.icon_path}
                                                        alt={brand.name} /> : (
                                                        <PictureOutlined className="w-full max-w-full"
                                                            style={{ fontSize: 56 }} />
                                                    )}
                                            </div>

                                            <div className="flex flex-col flex-grow items-center justify-center gap-0.5">
                                                <p className="text-base">{brand.name || brand.domain}</p>
                                                {brand.name && brand.domain && (
                                                    <p className="text-xxs whitespace-nowrap">
                                                        {brand.domain}
                                                    </p>
                                                )}
                                            </div>
                                            {isProfileMatch ? (
                                                <div
                                                    className="flex flex-row gap-1 items-center justify-end absolute top-[3px] right-[3px]">
                                                    <i className="fas fa-check text-xs text-info self-start" />
                                                </div>
                                            ) : (
                                                <div className="absolute top-[4px] right-[4px]">
                                                    <Popconfirm
                                                        title={(
                                                            <p style={{ lineHeight: '20px' }} className="max-w-64">
                                                                Are you sure you want to add this brand to your profile?
                                                            </p>
                                                        )}
                                                        getPopupContainer={() => document.getElementById(`brand-${index}-${brand.id}`)}
                                                        icon={<QuestionCircleFilled style={{ lineHeight: '20px', fontSize: 18, color: TB.config('color.warning') }} />}
                                                        popupVisible={isDisabled ? false : undefined}
                                                        okButtonProps={{ className: 'mt-4 text-xs' }}
                                                        cancelButtonProps={{ className: 'mt-4 text-xs' }}
                                                        okText="Yes, Continue"
                                                        disabled={isDisabled}
                                                        onConfirm={() => {
                                                            let brands = [...(auth.brands || []), brand];
                                                            onUpdateItem({ brands })
                                                            return true
                                                        }}
                                                    >
                                                        <p className="text-info clickable text-xxs underline">
                                                            Add to Profile
                                                        </p>
                                                    </Popconfirm>
                                                    {/*<button onClick={() => {*/}
                                                    {/*    let brands = [...(auth.brands || []), brand];*/}
                                                    {/*    return onUpdateItem({brands})*/}
                                                    {/*}} disabled={isDisabled} className="btn-link btn-link-primary text-xxs italic py-0">*/}
                                                    {/*    Select*/}
                                                    {/*</button>*/}
                                                </div>
                                            )}

                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    )

};

JobMatchForm.propTypes = {
    job: PropTypes.object,
    onUpdateMatching: PropTypes.func,
};

export default JobMatchForm;